import { Injectable, signal, computed } from '@angular/core';

export interface PasswordStrength {
    score: number; // 0 (empty) to 4 (strong)
    feedback: string[];
    isValid: boolean;
    strength: string
}

@Injectable({
    providedIn: 'root'
})
export class PasswordStrengthService {
    // Signal to store password
    private password = signal<string>('');

    // Computed signal for strength evaluation
    strength = computed<PasswordStrength>(() => this.evaluatePassword(this.password()));

    // Update password
    setPassword(password: string) {
        this.password.set(password);
    }

    private evaluatePassword(password: string): PasswordStrength {
        const feedback: string[] = [];
        let score = 0;
        let strength  = this.getStrengthLevel(0)

        if (!password) {
            return { score: 0, feedback: ['Password is required'], isValid: false, strength };
        }

        // Criteria checks
        if (password.length < 8) {
            feedback.push('Password must be at least 8 characters long');
        } else {
            score += 1;
        }

        if (!/[A-Z]/.test(password)) {
            feedback.push('Password must contain at least one uppercase letter');
        } else {
            score += 1;
        }

        if (!/[0-9]/.test(password)) {
            feedback.push('Password must contain at least one number');
        } else {
            score += 1;
        }

        if (!/[^A-Za-z0-9]/.test(password)) {
            feedback.push('Password must contain at least one special character');
        } else {
            score += 1;
        }

        // Additional feedback for very strong passwords
        if (password.length >= 12 && score === 4) {
            feedback.length = 0; // Clear feedback for very strong passwords
            // feedback.push('Password is very strong!');
        }

        return {
            score,
            feedback,
            isValid: score >= 3 && password.length >= 8,
            strength: this.getStrengthLevel(score)
        };
    }

    // Get strength level (for UI, e.g., weak, medium, strong)
    getStrengthLevel(score: number): string {
        switch (score) {
            case 0:
            case 1:
                return 'weak';
            case 2:
                return 'medium';
            case 3:
                return 'good';
            case 4:
                return 'strong';
            default:
                return 'weak';
        }
    }
}